import React, { useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { format as formatDate, isBefore, parseISO } from 'date-fns';

import {
  closeAndDownloadBudget,
  closeAndLoadBudget,
  downloadBudget,
  getUserData,
  loadAllFiles,
  loadBudget,
  pushModal,
  signOut,
} from 'loot-core/client/actions';

import { useAuth } from '../../hooks/useAuth';
import { useInitialMount } from '../../hooks/useInitialMount';
import { AnimatedLoading } from '../../icons/AnimatedLoading';
import {
  SvgCloudCheck,
  SvgCloudDownload,
  SvgDotsHorizontalTriple,
  SvgFileDouble,
} from '../../icons/v1';
import { SvgCloudUnknown, SvgKey, SvgRefreshArrow } from '../../icons/v2';
import { useResponsive } from '../../ResponsiveProvider';
import { styles, theme } from '../../style';
import { gsGreen700 } from '../../style/themes/light';
import { tokens } from '../../tokens';
import { Block } from '../common/Block';
import { Button } from '../common/Button';
import { Card } from '../common/Card';
import { Link } from '../common/Link';
import { Menu } from '../common/Menu';
import { Popover } from '../common/Popover';
import { Text } from '../common/Text';
import { View } from '../common/View';

function getFileDescription(file) {
  if (file.state === 'unknown') {
    return (
      'This is a cloud-based file but its state is unknown because you ' +
      'are offline.'
    );
  }

  if (file.encryptKeyId) {
    if (file.hasKey) {
      return 'This file is encrypted and you have key to access it.';
    }
    return 'This file is encrypted and you do not have the key for it.';
  }

  return null;
}

function FileMenu({ onDelete, onClose }) {
  function onMenuSelect(type) {
    onClose();

    switch (type) {
      case 'delete':
        onDelete();
        break;
      default:
    }
  }

  const items = [{ name: 'delete', text: 'Delete' }];
  const { isNarrowWidth } = useResponsive();

  const defaultMenuItemStyle = isNarrowWidth
    ? {
        ...styles.mobileMenuItem,
        color: theme.menuItemText,
        borderRadius: 0,
        borderTop: `1px solid ${theme.pillBorder}`,
      }
    : {};

  return (
    <Menu
      getItemStyle={() => defaultMenuItemStyle}
      onMenuSelect={onMenuSelect}
      items={items}
    />
  );
}

function FileMenuButton({ state, onDelete }) {
  const triggerRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <View>
      <Button
        ref={triggerRef}
        type="bare"
        aria-label="Menu"
        onClick={e => {
          e.stopPropagation();
          setMenuOpen(true);
        }}
      >
        <SvgDotsHorizontalTriple style={{ width: 16, height: 16 }} />
      </Button>

      <Popover
        triggerRef={triggerRef}
        isOpen={menuOpen}
        onOpenChange={() => setMenuOpen(false)}
      >
        <FileMenu
          state={state}
          onDelete={onDelete}
          onClose={() => setMenuOpen(false)}
        />
      </Popover>
    </View>
  );
}

function FileState({ file }) {
  const isBillingActive = useSelector(
    state => state.auth.billingStatus?.isActive,
  );

  let Icon;
  let status;
  let color;

  if (isBillingActive) {
    switch (file.state) {
      case 'unknown':
        Icon = SvgCloudUnknown;
        status = 'Device sync is not active';
        color = theme.buttonNormalDisabledText;
        break;
      case 'remote':
        Icon = SvgCloudDownload;
        status = 'Device synced';
        break;
      case 'local':
      case 'broken':
        Icon = SvgFileDouble;
        status = 'Local only';
        break;
      default:
        Icon = SvgCloudCheck;
        status = 'Device synced';
        break;
    }
  } else {
    Icon = SvgCloudUnknown;
    status = 'Device sync inactive (no subscription)';
  }

  return (
    <View
      style={{
        color,
        alignItems: 'center',
        flexDirection: 'row',
        marginTop: 8,
      }}
    >
      <Icon
        style={{
          width: 18,
          height: 18,
          color: 'currentColor',
        }}
      />

      <Text style={{ marginLeft: 5 }}>{status}</Text>
    </View>
  );
}

function File({ file, quickSwitchMode, onSelect, onDelete }) {
  const selecting = useRef(false);

  async function _onSelect(file) {
    // Never allow selecting the file while uploading/downloading, and
    // make sure to never allow duplicate clicks
    if (!selecting.current) {
      selecting.current = true;
      await onSelect(file);
      selecting.current = false;
    }
  }

  return (
    <View
      onClick={() => _onSelect(file)}
      title={getFileDescription(file)}
      style={{
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        boxShadow: 'rgba(99, 99, 99, 0.2) 0px 2px 8px 0px',
        margin: '0px 16px',
        padding: '12px 15px',
        backgroundColor: theme.buttonNormalBackground,
        borderRadius: 6,
        flexShrink: 0,
        cursor: 'pointer',
        ':hover': {
          backgroundColor: theme.hover,
        },
      }}
    >
      <View style={{ alignItems: 'flex-start' }}>
        <Text style={{ fontSize: 16, fontWeight: 700 }}>{file.name}</Text>

        <FileState file={file} />
        {file.createdAt ? (
          <View style={{ marginTop: 8, flexDirection: 'row', gap: 5 }}>
            <label>Created at:</label>
            {formatDate(parseISO(file.createdAt), 'MMM dd, yyyy HH:mm')}
          </View>
        ) : null}
      </View>

      <View
        style={{ flex: '0 0 auto', flexDirection: 'row', alignItems: 'center' }}
      >
        {file.encryptKeyId && (
          <SvgKey
            style={{
              width: 13,
              height: 13,
              marginRight: 8,
              color: file.hasKey
                ? theme.formLabelText
                : theme.buttonNormalDisabledText,
            }}
          />
        )}

        {!quickSwitchMode && (
          <FileMenuButton state={file.state} onDelete={() => onDelete(file)} />
        )}
      </View>
    </View>
  );
}

function BudgetFiles({ files, quickSwitchMode, onSelect, onDelete }) {
  return (
    <View
      style={{
        flexGrow: 1,
        [`@media (min-width: ${tokens.breakpoint_small})`]: {
          flexGrow: 0,
          maxHeight: 310,
        },
        overflow: 'auto',
        '& *': { userSelect: 'none' },
        padding: '16px 0px',
        gap: 16,
      }}
    >
      {!files || files.length === 0 ? (
        <Text
          style={{
            ...styles.mediumText,
            textAlign: 'center',
            color: theme.pageTextSubdued,
          }}
        >
          No budget files
        </Text>
      ) : (
        files
          .sort((a, b) => {
            if (!a.createdAt && !b.createdAt) {
              return 0;
            } else if (!a.createdAt) {
              return 1;
            } else if (!b.createdAt) {
              return -1;
            } else {
              return isBefore(new Date(a.createdAt), new Date(b.createdAt))
                ? 1
                : -1;
            }
          })
          .map(file => (
            <File
              key={file.id || file.cloudFileId}
              file={file}
              quickSwitchMode={quickSwitchMode}
              onSelect={onSelect}
              onDelete={onDelete}
            />
          ))
      )}
    </View>
  );
}

function RefreshButton({ style, onRefresh }) {
  const [loading, setLoading] = useState(false);

  async function _onRefresh() {
    setLoading(true);
    await onRefresh();
    setLoading(false);
  }

  const Icon = loading ? AnimatedLoading : SvgRefreshArrow;

  return (
    <Button
      type="bare"
      aria-label="Refresh"
      style={{ padding: 10, ...style }}
      onClick={_onRefresh}
    >
      <Icon style={{ width: 18, height: 18 }} />
    </Button>
  );
}

function BudgetListHeader({ quickSwitchMode, onRefresh }) {
  return (
    <View
      style={{
        flexDirection: 'row',
        justifyContent: 'space-between',
        padding: '12px 16px',
      }}
    >
      <Text
        style={{
          ...styles.veryLargeText,
        }}
      >
        Files
      </Text>
      {!quickSwitchMode && <RefreshButton onRefresh={onRefresh} />}
    </View>
  );
}

export function BudgetList({ showHeader = true, quickSwitchMode = false }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const allFiles = useSelector(state => state.budgets.allFiles || []);
  const { refreshAuthState } = useAuth();
  const userId = useSelector(state => state.user?.data?.id || null);

  const files = allFiles.filter(f => f.userId === userId || !f.cloudFileId);

  const { isNarrowWidth } = useResponsive();
  const narrowButtonStyle = isNarrowWidth
    ? {
        height: styles.mobileMinHeight,
      }
    : {};

  const onCreate = ({ testMode } = {}) => {
    navigate('/onboarding');
  };

  const refresh = () => {
    dispatch(getUserData());
    dispatch(loadAllFiles());
  };

  const onLogoutClick = async () => {
    await dispatch(signOut());
    await refreshAuthState();
  };

  const initialMount = useInitialMount();
  if (initialMount && quickSwitchMode) {
    refresh();
  }

  return (
    <Card
      style={{
        justifyContent: 'center',
        ...(!quickSwitchMode && {
          marginTop: 20,
          width: '100vw',
        }),
        [`@media (min-width: ${tokens.breakpoint_small})`]: {
          maxWidth: tokens.breakpoint_small,
          width: '100%',
        },
        margin: 0,
        padding: '16px 0px',
      }}
      childWrapperStyles={{ flex: 1 }}
    >
      {showHeader && (
        <BudgetListHeader
          quickSwitchMode={quickSwitchMode}
          onRefresh={refresh}
        />
      )}
      {!userId ? (
        <View
          style={{
            flexGrow: 1,
            [`@media (min-width: ${tokens.breakpoint_small})`]: {
              flexGrow: 0,
              height: 310,
            },
            gap: 20,
            justifyContent: 'center',
            alignItems: 'center',
            ...styles.delayedFadeIn,
          }}
        >
          <Block style={{ marginBottom: 20, fontSize: 18 }}>
            Loading files...
          </Block>
          <AnimatedLoading width={25} color={theme.pageTextDark} />
        </View>
      ) : (
        <BudgetFiles
          files={files}
          quickSwitchMode={quickSwitchMode}
          onSelect={file => {
            if (file.state === 'remote') {
              dispatch(downloadBudget(file.cloudFileId));
            } else {
              dispatch(loadBudget(file.id));
            }
          }}
          onDelete={file => dispatch(pushModal('delete-budget', { file }))}
        />
      )}
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 16px',
          gap: 15,
        }}
      >
        <Button type="bare" onClick={onLogoutClick}>
          Logout
        </Button>
        <Button type="primary" onClick={onCreate} style={narrowButtonStyle}>
          Create new file
        </Button>
      </View>
      <View style={{ alignItems: 'center', padding: '0 15px' }}>
        <span style={{ textAlign: 'center' }}>
          You can manage your subscription by going to
          <span style={{ marginLeft: 3, marginRight: 3 }}>
            <Link
              to="/billing"
              variant="internal"
              style={{ color: gsGreen700 }}
            >
              billing
            </Link>
          </span>
          page.
        </span>
      </View>
    </Card>
  );
}
